import { Survey } from "../../../pages/ProjectSurveysV2/types";
import AccordianDrop from "@components/AccordianDrop/AccordianDrop";
import { getOrganizedPhasesFromListOfServices } from "@utils/helpers";
import { Service } from "../../../pages/Projects/ServicesV2/types";
import { ColumnDef } from "@tanstack/react-table";
import { snakeToText } from "@utils/helperFunctions";
import DataTable from "@components/DataTable/DataTable";
import {
  recommendationsTable,
  nonServiceRecommendationsDisplay,
} from "./helpers";
import "../style.css";

interface SurveyRecommendationsProps {
  columns: ColumnDef<Service>[];
  recommendations: Survey["recommendations"];
  nonServiceRecommendations: Survey["recommendations"];
  calculations: Survey["calculations"];
  responses: Survey["responses"];
}

const SurveyRecommendations = ({
  columns,
  recommendations,
  nonServiceRecommendations,
  calculations,
  responses,
}: SurveyRecommendationsProps) => {
  const phases = getOrganizedPhasesFromListOfServices(
    recommendations as Service[]
  );
  const pendingServiceItems = recommendations?.some(
    (rec) => rec.status === "pending"
  );

  return (
    <div className="surveyRecommendations">
      {/* Non-service recommendations */}
      {nonServiceRecommendations?.length ? (
        nonServiceRecommendationsDisplay(nonServiceRecommendations)
      ) : (
        <></>
      )}

      {/* Service recommendations */}
      {recommendations?.length ? (
        <AccordianDrop
          header={
            <div className="accordionHeaderContent recommendationAccordion">
              <div className="detail">
                {" "}
                <div className="accordianTitle">Recommended Services</div>
                <div className="accordianSubtitle">
                  Expand to view services that will be applied to project
                </div>
              </div>
              {pendingServiceItems && (
                <div className="pendingAlert">Pending Recommendations</div>
              )}
            </div>
          }
          body={recommendationsTable(recommendations, phases, columns)}
          startingAccordionStateIsOpen
          className=""
          closeable={true}
          setAccordianIsOpen={() => null}
        />
      ) : (
        <></>
      )}

      {/* Calculations */}
      <AccordianDrop
        header={
          <div className="accordionHeaderContent">
            <div className="accordianTitle">Calculations</div>
            <div className="accordianSubtitle">
              Expand to view calculations that resulted in the recommendations
            </div>
          </div>
        }
        body={
          <DataTable
            data={
              calculations?.length
                ? calculations.map((c, i) => {
                    return { ...c, id: i + 1 };
                  })
                : [
                    {
                      id: 0,
                      calculation_id: "No calculations",
                      value: "",
                    },
                  ]
            }
            columns={[
              {
                header: "Calculation Name",
                cell(props) {
                  const id = props.row.original.id;
                  const name = props.row.original?.calculation_id;
                  return id === 0
                    ? name
                    : snakeToText(props.row.original?.calculation_id || "");
                },
              },
              {
                header: "Calculated Value",
                cell(props) {
                  return props.row.original?.value || "";
                },
              },
            ]}
            striped
          />
        }
        className=""
        setAccordianIsOpen={() => null}
      />

      {/* Responses */}
      <AccordianDrop
        header={
          <div className="accordionHeaderContent">
            <div className="accordianTitle">Responses</div>
            <div className="accordianSubtitle">
              Expand to view responses that were submitted for the survey
            </div>
          </div>
        }
        body={
          <DataTable
            data={
              responses?.length
                ? responses
                    .filter((r) => r.answer)
                    .map((q, i) => {
                      return { ...q, id: i + 1 };
                    })
                : [{ id: 0, question: "No responses", answer: "" }]
            }
            columns={[
              {
                header: "Question",
                cell(props) {
                  return props.row.original?.question || "";
                },
              },
              {
                header: "Response",
                cell(props) {
                  return props.row.original?.answer || "";
                },
              },
            ]}
            striped
          />
        }
        className=""
        setAccordianIsOpen={() => null}
      />
    </div>
  );
};

export default SurveyRecommendations;
